'use client';

import { useLayoutEffect, useRef } from 'react';
import gsap from 'gsap';
import styles from './style.module.scss';

type AllowedTags = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'a';

type AnimationType =
  | 'zoomIn'
  | 'zoomOut'
  | 'slideInLeft'
  | 'slideInRight'
  | 'slideInTop'
  | 'slideInBottom';

interface AnimatedTextProps {
  children: React.ReactNode;
  className?: string;
  animationType?: AnimationType;
  animationDuration?: number;
  animationDelay?: number;
  variant?: AllowedTags;
  href?: string;
  target?: string;
  rel?: string;
  style?: React.CSSProperties;
}

const getFromVars = (animationType: AnimationType): gsap.TweenVars => {
  switch (animationType) {
    case 'zoomOut':
      return { autoAlpha: 0, scale: 1.2 };
    case 'slideInLeft':
      return { autoAlpha: 0, x: -80 };
    case 'slideInRight':
      return { autoAlpha: 0, x: 80 };
    case 'slideInTop':
      return { autoAlpha: 0, y: -80 };
    case 'slideInBottom':
      return { autoAlpha: 0, y: 80 };
    case 'zoomIn':
    default:
      return { autoAlpha: 0, scale: 0.6 };
  }
};
const revealCallbacks = new WeakMap<Element, () => void>();
let sharedObserver: IntersectionObserver | null = null;

const getObserver = (): IntersectionObserver | null => {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
    return null;
  }
  if (!sharedObserver) {
    sharedObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const cb = revealCallbacks.get(entry.target);
          if (cb) {
            cb();
            revealCallbacks.delete(entry.target);
            sharedObserver?.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px 0px 0px', threshold: 0 }
    );
  }
  return sharedObserver;
};

const ParallaxText: React.FC<AnimatedTextProps> = ({
  children,
  variant = 'p',
  className = '',
  animationType = 'zoomIn',
  animationDuration = 900,
  animationDelay = 0,
  href,
  target = '_self',
  rel = 'noopener noreferrer',
  style,
}) => {
  const elementRef = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    const el = elementRef.current;
    if (!el) return;

    const fromVars = getFromVars(animationType);
    gsap.set(el, {
      ...fromVars,
      force3D: true,
      willChange: 'transform, opacity',
    });

    const reveal = () => {
      gsap.to(el, {
        autoAlpha: 1,
        x: 0,
        y: 0,
        scale: 1,
        duration: (animationDuration || 900) / 1000,
        delay: animationDelay / 1000,
        ease: 'power2.out',
        force3D: true,
        onComplete: () => gsap.set(el, { clearProps: 'willChange' }),
      });
    };

    const observer = getObserver();
    if (!observer) {
      reveal();
      return;
    }
    const rect = el.getBoundingClientRect();
    if (rect.top <= window.innerHeight * 0.9 && rect.bottom >= 0) {
      reveal();
      return;
    }

    revealCallbacks.set(el, reveal);
    observer.observe(el);

    return () => {
      revealCallbacks.delete(el);
      observer.unobserve(el);
      gsap.killTweensOf(el);
    };
  }, [animationType, animationDuration, animationDelay]);

  const Tag = variant;

  return (
    <Tag
      ref={(el: HTMLElement | null) => {
        elementRef.current = el;
      }}
      {...(Tag === 'a' ? { href, target, rel } : {})}
      className={`${styles.animatedText} ${className}`}
      style={style}
    >
      {children}
    </Tag>
  );
};

export default ParallaxText;
